import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AudioLines, Captions, Eye, FileVideo, LoaderCircle, Pencil, Trash2, Upload } from 'lucide-react';
import { Modale } from '../ui/Modale';
import { useToast } from '../ui/useToast';
import { ErreurApi } from '../../lib/api';
import {
  annoncerVideoLecon,
  envoyerVideoLecon,
  lireSousTitres,
  useApresDepot,
  useGesteContenu,
  type Cours,
  type LeconCours,
} from '../../lib/useCours';
import { dateCourte, dureeLisible, heureReunion, tailleLisible } from '../../lib/useElearning';
import { horodatageReunion } from '../../lib/emargement-affichage';
import { adresseLecon } from '../../lib/useLecteur';
import { RessourcesLeconCours } from './RessourcesLeconCours';

const FORMATS_VIDEO = 'video/mp4,.mp4,.m4v';
const FORMATS_SOUS_TITRES = '.vtt,.srt,text/vtt';

const CLASSE_ACTION =
  'inline-flex min-h-[36px] items-center gap-1.5 rounded-lg px-2.5 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 [@media(pointer:coarse)]:min-h-[40px]';
const ACTION_PRINCIPALE = `${CLASSE_ACTION} bg-codialis-navy text-white hover:bg-codialis-deep`;
const ACTION_SECONDAIRE = `${CLASSE_ACTION} border border-gray-200 bg-white text-codialis-navy hover:bg-gray-50`;
const ACTION_RETRAIT = `${CLASSE_ACTION} text-red-600 hover:bg-red-50`;

interface EnvoiEnCours {
  leconId: string;
  nom: string;
  recus: number;
  total: number;
}

function messageErreur(erreur: unknown, defaut: string): string {
  return erreur instanceof ErreurApi ? erreur.message : defaut;
}

function pourcentage(recus: number, total: number): number {
  return total > 0 ? Math.floor((recus / total) * 100) : 0;
}

/** « Transcription automatique · le 23/10/2026 », « saluer.vtt · déposés le … », correction comprise. */
function texteDeLaLecon(sousTitres: NonNullable<LeconCours['sousTitres']>): string {
  const origine =
    sousTitres.source === 'TRANSCRIPTION'
      ? `Transcription automatique · le ${dateCourte(sousTitres.deposesLe)}`
      : `${sousTitres.nomFichier ?? 'Fichier'} · déposés le ${dateCourte(sousTitres.deposesLe)}`;
  return sousTitres.corrigesLe ? `${origine} · corrigés le ${horodatageReunion(sousTitres.corrigesLe)}` : origine;
}

/** Ce que l'auteur perd si une transcription remplace le texte en place. */
function texteRemplace(sousTitres: NonNullable<LeconCours['sousTitres']>): string {
  const texte =
    sousTitres.source === 'TRANSCRIPTION' ? 'la transcription actuelle' : `les sous-titres de « ${sousTitres.nomFichier} »`;
  return sousTitres.corrigesLe ? `${texte}, corrections comprises,` : texte;
}

/** Où en est la transcription automatique de la vidéo, ou `null` s'il n'y a rien à en dire. */
function EtatTranscription({ lecon, raccordee }: { lecon: LeconCours; raccordee: boolean }): JSX.Element | null {
  const { statut, message, debuteeLe } = lecon.transcription;
  if (statut === 'EN_ATTENTE' || statut === 'EN_COURS') {
    return (
      <div className="flex items-start gap-1.5 text-[11px] font-semibold text-codialis-sky" role="status">
        <LoaderCircle className="mt-px h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden />
        <span>
          {statut === 'EN_COURS' && debuteeLe
            ? `Transcription en cours depuis ${heureReunion(debuteeLe)}`
            : 'Transcription en attente'}
          {message ? ` · ${message}` : ''}
        </span>
      </div>
    );
  }
  if (statut === 'ECHEC' || statut === 'REFUSEE') {
    return (
      <p className="text-[11px] font-semibold text-red-700">
        Transcription {statut === 'ECHEC' ? 'en échec' : 'refusée'} : {message}
      </p>
    );
  }
  if (!raccordee && lecon.video && !lecon.sousTitres) {
    return <p className="text-[11px] text-codialis-gray">Transcription automatique non raccordée : déposez un fichier .vtt ou .srt.</p>;
  }
  return null;
}

/**
 * Étape « Contenus & supports » de « Créer un cours » (TF-444) : la vidéo de
 * chaque leçon, déposée par morceaux et reprenable après une coupure, ses
 * sous-titres et ses ressources annexes (TF-447). Une vidéo à la fois ;
 * l'aperçu ouvre la leçon dans le lecteur, telle que l'apprenant la verra. Un
 * cours archivé s'affiche sans geste.
 */
export function ContenusCours({ cours }: { cours: Cours }): JSX.Element {
  const toast = useToast();
  const navigate = useNavigate();
  const gestes = useGesteContenu(cours.id);
  const apresDepot = useApresDepot(cours.id);
  const [envoi, setEnvoi] = useState<EnvoiEnCours | null>(null);
  const [erreurs, setErreurs] = useState<Record<string, string>>({});
  const [aRetirer, setARetirer] = useState<LeconCours | null>(null);
  const [aRelancer, setARelancer] = useState<LeconCours | null>(null);
  const [aRemplacer, setARemplacer] = useState<LeconCours | null>(null);
  const annulation = useRef<AbortController | null>(null);
  const choixVideo = useRef<HTMLInputElement>(null);
  const choixSousTitres = useRef<HTMLInputElement>(null);
  const cible = useRef<{ lecon: LeconCours; reprise: boolean } | null>(null);
  const lectureSeule = cours.refusModification !== null;

  useEffect(() => () => annulation.current?.abort(), []);

  // Quitter la page pendant un envoi l'interromprait : le navigateur le demande.
  useEffect(() => {
    if (!envoi) return;
    const retenir = (evenement: BeforeUnloadEvent) => {
      evenement.preventDefault();
      evenement.returnValue = '';
    };
    window.addEventListener('beforeunload', retenir);
    return () => window.removeEventListener('beforeunload', retenir);
  }, [envoi]);

  const signaler = (leconId: string, message: string | null) =>
    setErreurs((actuelles) => {
      const suivantes = { ...actuelles };
      if (message) suivantes[leconId] = message;
      else delete suivantes[leconId];
      return suivantes;
    });

  const envoyer = async (lecon: LeconCours, fichier: File, reprise: boolean) => {
    signaler(lecon.id, null);
    if (reprise && lecon.depot && fichier.size !== lecon.depot.attendus) {
      signaler(
        lecon.id,
        `Ce n’est pas le même fichier : le dépôt de « ${lecon.depot.nomFichier} » attend ${tailleLisible(lecon.depot.attendus) ?? `${lecon.depot.attendus} octets`}.`,
      );
      return;
    }
    const controleur = new AbortController();
    annulation.current = controleur;
    setEnvoi({ leconId: lecon.id, nom: fichier.name, recus: 0, total: fichier.size });
    try {
      if (!reprise) apresDepot.remplacer(await annoncerVideoLecon(cours.id, lecon.id, fichier));
      const fini = await envoyerVideoLecon(
        cours.id,
        lecon.id,
        fichier,
        (recus) => setEnvoi((actuel) => (actuel ? { ...actuel, recus } : actuel)),
        controleur.signal,
      );
      apresDepot.remplacer(fini);
      toast(lecon.video ? 'Vidéo remplacée' : 'Vidéo déposée · lisible dans le lecteur');
    } catch (cause) {
      signaler(
        lecon.id,
        controleur.signal.aborted
          ? 'Envoi interrompu : reprenez-le avec le même fichier, il repartira du dernier morceau reçu.'
          : messageErreur(cause, 'L’envoi a échoué : reprenez-le, il repartira du dernier morceau reçu.'),
      );
      void apresDepot.relire();
    } finally {
      annulation.current = null;
      setEnvoi(null);
    }
  };

  const choisirVideo = (lecon: LeconCours, reprise: boolean) => {
    cible.current = { lecon, reprise };
    choixVideo.current?.click();
  };

  /** Une nouvelle vidéo transcrite remplace le texte en place : l'auteur le confirme d'abord. */
  const deposerVideo = (lecon: LeconCours) => {
    if (cours.transcriptionRaccordee && lecon.sousTitres) setARemplacer(lecon);
    else choisirVideo(lecon, false);
  };

  const choisirSousTitres = (lecon: LeconCours) => {
    cible.current = { lecon, reprise: false };
    choixSousTitres.current?.click();
  };

  const deposerSousTitres = async (lecon: LeconCours, fichier: File) => {
    signaler(lecon.id, null);
    try {
      await gestes.mutateAsync({
        geste: 'deposer-sous-titres',
        leconId: lecon.id,
        nomFichier: fichier.name,
        contenu: await lireSousTitres(fichier),
      });
      toast('Sous-titres enregistrés');
    } catch (cause) {
      signaler(lecon.id, messageErreur(cause, 'Les sous-titres n’ont pas pu être enregistrés.'));
    }
  };

  const geste = async (
    lecon: LeconCours,
    action: 'annuler-depot' | 'retirer-video' | 'retirer-sous-titres' | 'lancer-transcription',
    succes: string,
  ) => {
    signaler(lecon.id, null);
    try {
      await gestes.mutateAsync({ geste: action, leconId: lecon.id });
      toast(succes);
    } catch (cause) {
      signaler(lecon.id, messageErreur(cause, 'Le geste n’a pas pu être enregistré.'));
    }
  };

  const confirmerRetrait = async () => {
    const lecon = aRetirer;
    setARetirer(null);
    if (!lecon) return;
    await geste(lecon, 'retirer-video', 'Vidéo retirée · fichier effacé');
  };

  const lancerTranscription = (lecon: LeconCours) => {
    if (lecon.sousTitres) setARelancer(lecon);
    else void geste(lecon, 'lancer-transcription', 'Transcription demandée');
  };

  const confirmerRelance = async () => {
    const lecon = aRelancer;
    setARelancer(null);
    if (!lecon) return;
    await geste(lecon, 'lancer-transcription', 'Transcription demandée');
  };

  const confirmerRemplacement = () => {
    const lecon = aRemplacer;
    setARemplacer(null);
    if (lecon) choisirVideo(lecon, false);
  };

  if (cours.modules.length === 0) {
    return (
      <p className="rounded-xl bg-gray-50 px-4 py-3 text-sm text-codialis-gray">
        Le programme est vide : ajoutez d’abord des modules et des leçons à l’étape 2.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-codialis-gray">
        Déposez la vidéo de chaque leçon : MP4, de préférence encodé en H.264 (lisible sur ordinateur, tablette et
        téléphone), 4 Go au plus.{' '}
        {cours.transcriptionRaccordee
          ? 'Chaque vidéo est transcrite automatiquement : la transcription s’affiche sous la vidéo et sert de sous-titres. Corrigez-la depuis l’aperçu, ou déposez vos propres sous-titres en .vtt ou .srt.'
          : 'Les sous-titres se joignent en .vtt ou .srt ; l’apprenant les active dans le lecteur et les lit sous la vidéo.'}{' '}
        Joignez aussi à chaque leçon ses ressources — {cours.limitesRessources.libelleFormats} ({cours.limitesRessources.libelleTailleMax} au
        plus) ou liens : l’apprenant les consulte et les télécharge sous la vidéo.
      </p>

      <input
        ref={choixVideo}
        type="file"
        accept={FORMATS_VIDEO}
        className="hidden"
        onChange={(evenement) => {
          const fichier = evenement.target.files?.[0];
          evenement.target.value = '';
          const choisi = cible.current;
          if (fichier && choisi) void envoyer(choisi.lecon, fichier, choisi.reprise);
        }}
      />
      <input
        ref={choixSousTitres}
        type="file"
        accept={FORMATS_SOUS_TITRES}
        className="hidden"
        onChange={(evenement) => {
          const fichier = evenement.target.files?.[0];
          evenement.target.value = '';
          const choisi = cible.current;
          if (fichier && choisi) void deposerSousTitres(choisi.lecon, fichier);
        }}
      />

      <ol className="space-y-3">
        {cours.modules.map((module, rangModule) => (
          <li key={module.id} className="rounded-xl border border-gray-100 bg-white">
            <div className="flex items-center gap-3 p-2 sm:p-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-codialis-sky text-xs font-bold text-white">
                {rangModule + 1}
              </div>
              <div className="min-w-0 flex-1 truncate text-sm font-semibold text-codialis-navy">{module.titre}</div>
            </div>
            {module.lecons.length === 0 ? (
              <p className="px-3 pb-3 text-xs text-codialis-gray">Aucune leçon dans ce module.</p>
            ) : (
              <ol className="space-y-2 px-2 pb-2 sm:px-3 sm:pb-3">
                {module.lecons.map((lecon, rangLecon) => {
                  const enEnvoi = envoi?.leconId === lecon.id ? envoi : null;
                  const occupe = envoi !== null || gestes.isPending;
                  const details = lecon.video
                    ? [lecon.video.nomFichier, dureeLisible(lecon.video.dureeSecondes), tailleLisible(lecon.video.tailleOctets)]
                        .filter(Boolean)
                        .join(' · ')
                    : null;
                  return (
                    <li key={lecon.id} className="rounded-lg bg-gray-50 p-2 sm:p-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="w-8 shrink-0 font-mono text-xs text-codialis-gray">
                          {rangModule + 1}.{rangLecon + 1}
                        </span>
                        <span className="min-w-0 flex-1 truncate text-sm font-medium text-codialis-navy">{lecon.titre}</span>
                        {(lecon.video || lecon.ressources.length > 0) && (
                          <button
                            type="button"
                            onClick={() => navigate(adresseLecon(lecon.id))}
                            className={`${CLASSE_ACTION} text-codialis-sky hover:bg-white`}
                          >
                            <Eye className="h-4 w-4" aria-hidden />
                            Aperçu
                          </button>
                        )}
                      </div>

                      <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
                        <div className="flex min-w-0 flex-wrap items-center gap-2 rounded-lg bg-white p-2">
                          <FileVideo className="h-4 w-4 shrink-0 text-codialis-sky" aria-hidden />
                          <div className="min-w-0 flex-1 basis-32">
                            {enEnvoi ? (
                              <>
                                <div className="truncate text-xs font-semibold text-codialis-navy">
                                  Envoi de « {enEnvoi.nom} » · {pourcentage(enEnvoi.recus, enEnvoi.total)} %
                                </div>
                                <div
                                  className="progress-track mt-1 h-1.5 rounded-full"
                                  role="progressbar"
                                  aria-label={`Envoi de la vidéo de « ${lecon.titre} »`}
                                  aria-valuemin={0}
                                  aria-valuemax={100}
                                  aria-valuenow={pourcentage(enEnvoi.recus, enEnvoi.total)}
                                >
                                  <div
                                    className="progress-bar h-full rounded-full"
                                    style={{ width: `${pourcentage(enEnvoi.recus, enEnvoi.total)}%` }}
                                  />
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="truncate text-xs text-codialis-navy">{details ?? 'Aucune vidéo'}</div>
                                {lecon.depot && (
                                  <div className="text-[11px] font-semibold text-amber-700">
                                    {lecon.video ? 'Nouvel envoi interrompu' : 'Envoi interrompu'} à{' '}
                                    {pourcentage(lecon.depot.recus, lecon.depot.attendus)} % ·{' '}
                                    {lecon.depot.nomFichier}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                          {enEnvoi && (
                            <button type="button" onClick={() => annulation.current?.abort()} className={ACTION_SECONDAIRE}>
                              Interrompre
                            </button>
                          )}
                          {!enEnvoi && !lectureSeule && lecon.depot && (
                            <>
                              <button
                                type="button"
                                disabled={occupe}
                                onClick={() => choisirVideo(lecon, true)}
                                className={ACTION_PRINCIPALE}
                              >
                                <Upload className="h-4 w-4" aria-hidden />
                                Reprendre
                              </button>
                              <button
                                type="button"
                                disabled={occupe}
                                onClick={() => void geste(lecon, 'annuler-depot', 'Envoi annulé')}
                                className={ACTION_SECONDAIRE}
                              >
                                Annuler l’envoi
                              </button>
                            </>
                          )}
                          {!enEnvoi && !lectureSeule && !lecon.depot && (
                            <>
                              <button
                                type="button"
                                disabled={occupe}
                                onClick={() => deposerVideo(lecon)}
                                className={lecon.video ? ACTION_SECONDAIRE : ACTION_PRINCIPALE}
                              >
                                <Upload className="h-4 w-4" aria-hidden />
                                {lecon.video ? 'Remplacer' : 'Déposer une vidéo'}
                              </button>
                              {lecon.video && (
                                <button
                                  type="button"
                                  disabled={occupe}
                                  onClick={() => setARetirer(lecon)}
                                  className={ACTION_RETRAIT}
                                  aria-label={`Retirer la vidéo de « ${lecon.titre} »`}
                                >
                                  <Trash2 className="h-4 w-4" aria-hidden />
                                </button>
                              )}
                            </>
                          )}
                        </div>

                        <div className="flex min-w-0 flex-wrap items-center gap-2 rounded-lg bg-white p-2">
                          <Captions className="h-4 w-4 shrink-0 text-codialis-sky" aria-hidden />
                          <div className="min-w-0 flex-1 basis-48">
                            <div className="truncate text-xs text-codialis-navy">
                              {lecon.sousTitres ? texteDeLaLecon(lecon.sousTitres) : 'Aucun sous-titre'}
                            </div>
                            <EtatTranscription lecon={lecon} raccordee={cours.transcriptionRaccordee} />
                          </div>
                          {lecon.sousTitres && lecon.transcription.refusCorrection === null && (
                            <button
                              type="button"
                              onClick={() => navigate(adresseLecon(lecon.id, 'transcription'))}
                              className={ACTION_SECONDAIRE}
                            >
                              <Pencil className="h-4 w-4" aria-hidden />
                              Corriger
                            </button>
                          )}
                          {!lectureSeule && (
                            <>
                              {lecon.transcription.refusLancement === null && (
                                <button
                                  type="button"
                                  disabled={gestes.isPending}
                                  onClick={() => lancerTranscription(lecon)}
                                  className={ACTION_SECONDAIRE}
                                >
                                  <AudioLines className="h-4 w-4" aria-hidden />
                                  {lecon.transcription.statut ? 'Relancer' : 'Transcrire'}
                                </button>
                              )}
                              <button
                                type="button"
                                disabled={gestes.isPending}
                                onClick={() => choisirSousTitres(lecon)}
                                className={ACTION_SECONDAIRE}
                                aria-label={`${lecon.sousTitres ? 'Remplacer' : 'Déposer'} les sous-titres de « ${lecon.titre} » par un fichier`}
                              >
                                <Upload className="h-4 w-4" aria-hidden />
                                {lecon.sousTitres ? 'Remplacer' : 'Déposer'}
                              </button>
                              {lecon.sousTitres && (
                                <button
                                  type="button"
                                  disabled={gestes.isPending}
                                  onClick={() => void geste(lecon, 'retirer-sous-titres', 'Sous-titres retirés')}
                                  className={ACTION_RETRAIT}
                                  aria-label={`Retirer les sous-titres de « ${lecon.titre} »`}
                                >
                                  <Trash2 className="h-4 w-4" aria-hidden />
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </div>

                      <RessourcesLeconCours cours={cours} lecon={lecon} />

                      {erreurs[lecon.id] && (
                        <p className="mt-2 text-xs text-red-700" role="alert">
                          {erreurs[lecon.id]}
                        </p>
                      )}
                    </li>
                  );
                })}
              </ol>
            )}
          </li>
        ))}
      </ol>

      <Modale
        titre="Relancer la transcription ?"
        ouverte={aRelancer !== null}
        onFermer={() => setARelancer(null)}
        largeur="max-w-md"
      >
        <div className="space-y-4 p-4 sm:p-6">
          <p className="text-sm text-codialis-gray">
            La vidéo de « {aRelancer?.titre} » est transcrite à nouveau. Quand la transcription sera prête, elle
            remplacera {aRelancer?.sousTitres ? texteRemplace(aRelancer.sousTitres) : 'le texte actuel'} : sous la
            vidéo et dans les sous-titres. D’ici là, le texte actuel reste lu.
          </p>
          <div className="flex flex-wrap justify-end gap-2">
            <button type="button" onClick={() => setARelancer(null)} className={ACTION_SECONDAIRE}>
              Garder le texte actuel
            </button>
            <button type="button" onClick={() => void confirmerRelance()} className={ACTION_PRINCIPALE}>
              Relancer la transcription
            </button>
          </div>
        </div>
      </Modale>

      <Modale
        titre="Déposer une nouvelle vidéo ?"
        ouverte={aRemplacer !== null}
        onFermer={() => setARemplacer(null)}
        largeur="max-w-md"
      >
        <div className="space-y-4 p-4 sm:p-6">
          <p className="text-sm text-codialis-gray">
            La nouvelle vidéo de « {aRemplacer?.titre} » sera transcrite automatiquement :{' '}
            {aRemplacer?.sousTitres ? texteRemplace(aRemplacer.sousTitres) : 'le texte actuel'} sera retiré à la fin de
            l’envoi, puis remplacé par la transcription. Vous pourrez ensuite la corriger, ou redéposer un fichier de
            sous-titres.
          </p>
          <div className="flex flex-wrap justify-end gap-2">
            <button type="button" onClick={() => setARemplacer(null)} className={ACTION_SECONDAIRE}>
              Annuler
            </button>
            <button type="button" onClick={confirmerRemplacement} className={ACTION_PRINCIPALE}>
              Choisir la vidéo
            </button>
          </div>
        </div>
      </Modale>

      <Modale titre="Retirer la vidéo ?" ouverte={aRetirer !== null} onFermer={() => setARetirer(null)} largeur="max-w-md">
        <div className="space-y-4 p-4 sm:p-6">
          <p className="text-sm text-codialis-gray">
            La vidéo de « {aRetirer?.titre} » est effacée du serveur : les apprenants ne la verront plus. Ses
            sous-titres restent.
          </p>
          <div className="flex flex-wrap justify-end gap-2">
            <button type="button" onClick={() => setARetirer(null)} className={ACTION_SECONDAIRE}>
              Garder la vidéo
            </button>
            <button type="button" onClick={() => void confirmerRetrait()} className={`${CLASSE_ACTION} bg-red-600 text-white hover:bg-red-700`}>
              Retirer la vidéo
            </button>
          </div>
        </div>
      </Modale>
    </div>
  );
}
